import React from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb, TrendingUp, Quote as QuoteIcon, ArrowRight, FileText } from 'lucide-react';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/Reveal';
import { QUOTES } from '../mock';

const ARTICLES = [
  {
    title: 'Fees are going down, down, down…',
    tag: 'School fees · Issue 02',
    excerpt:
      'With 639 public and 580 private schools now across the UAE and 13,000 more Dubai seats coming, over-supply is reshaping fees. We examine how the market is responding.',
    cover: '/reports/ei-issue-2.png',
  },
  {
    title: 'The power is back with parents',
    tag: 'Admissions · Mystery Shopper',
    excerpt:
      'School marketing budgets doubled in a year and advertising spend topped an estimated $20m. Our mystery shoppers reveal what really happens through the admissions funnel.',
    cover: '/reports/mystery-shoppers.png',
  },
  {
    title: 'A recruitment ‘crisis’? The voices of teachers',
    tag: 'Teachers · Teaching Landscape',
    excerpt:
      'The UAE competes globally for teachers against Hong Kong, Doha, Sydney and London. We map the demographic landscape — and why teacher expertise is so often underestimated.',
    cover: '/reports/teaching-landscape.png',
  },
];

const THEMES = [
  { icon: Lightbulb, title: 'Challenge assumptions', text: 'We stress-test the “obvious”. Many sector truths don’t survive contact with the data.' },
  { icon: TrendingUp, title: 'Read the trends early', text: 'Spotting demand shifts, curricula movement and pricing pressure before they become consensus.' },
  { icon: QuoteIcon, title: 'Insight from the field', text: 'Our thinking comes from people embedded in schools, regulators and media — not a spreadsheet alone.' },
];

export default function CriticalThinking() {
  return (
    <div>
      <PageBanner
        title="Critical Thinking"
        subtitle="Perspectives, analysis and honest debate on the UAE education sector — drawn straight from our research."
        crumb="Critical Thinking"
        image="https://images.unsplash.com/photo-1608222351212-18fe0ec7b13b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxidXNpbmVzcyUyMGFuYWx5dGljc3xlbnwwfHx8fDE3OTA4NDkyNjR8MA&ixlib=rb-4.1.0&q=85"
      />

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {THEMES.map((t, i) => (
              <Reveal key={t.title} delay={i * 100}>
                <div className="bg-[#f8fafc] rounded-2xl p-8 h-full ring-1 ring-slate-200/70">
                  <div className="w-14 h-14 rounded-2xl bg-[#132b4d] text-white flex items-center justify-center mb-6">
                    <t.icon size={25} />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-[#132b4d] mb-3">{t.title}</h3>
                  <p className="text-slate-600 leading-relaxed text-sm">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Quote band */}
      <section className="py-20 bg-[#0f2340] relative overflow-hidden">
        <div className="eig-blob w-[420px] h-[420px] bg-[#1ca5dc]/25 -top-24 right-10" />
        <div className="max-w-5xl mx-auto px-5 lg:px-8 relative grid grid-cols-1 md:grid-cols-3 gap-8">
          {QUOTES.map((q, i) => (
            <Reveal key={q.text} delay={i * 100}>
              <figure className="bg-white/5 ring-1 ring-white/10 rounded-2xl p-7 h-full">
                <QuoteIcon size={26} className="text-[#5cc6ef] mb-4" />
                <blockquote className="text-white/90 leading-relaxed text-sm">“{q.text}”</blockquote>
                <figcaption className="mt-5 text-[11px] uppercase tracking-widest font-heading font-semibold text-[#5cc6ef]">
                  {q.source}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 eig-graph">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-14 max-w-2xl mx-auto">
            <span className="eig-eyebrow justify-center">Latest thinking</span>
            <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl text-[#132b4d]">Straight from the reports</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ARTICLES.map((a, i) => (
              <Reveal key={a.title} delay={i * 100}>
                <article className="group bg-white rounded-2xl overflow-hidden ring-1 ring-slate-200/70 hover:shadow-2xl hover:shadow-[#132b4d]/10 transition-all duration-300 h-full flex flex-col">
                  <div className="h-56 overflow-hidden bg-[#0f2340]">
                    <img src={a.cover} alt={a.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <span className="inline-flex items-center gap-1.5 text-xs font-heading font-semibold text-[#1ca5dc] uppercase tracking-wide mb-3">
                      <FileText size={13} /> {a.tag}
                    </span>
                    <h3 className="font-heading font-bold text-lg text-[#132b4d] mb-3 leading-snug">{a.title}</h3>
                    <p className="text-slate-600 leading-relaxed text-sm flex-1">{a.excerpt}</p>
                    <Link to="/reports" className="inline-flex items-center gap-2 mt-5 text-[#1ca5dc] font-heading font-semibold text-sm group-hover:gap-3 transition-all">
                      Read the report <ArrowRight size={16} />
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="bg-[#0f2340] rounded-[28px] p-10 md:p-14 text-center relative overflow-hidden">
            <div className="eig-blob w-[320px] h-[320px] bg-[#1ca5dc]/25 -bottom-20 left-10" />
            <div className="relative">
              <h3 className="text-white font-heading font-extrabold text-2xl md:text-3xl mb-4">Want analysis like this for your business?</h3>
              <p className="text-slate-300 max-w-2xl mx-auto mb-8">Commission EIG to turn these perspectives into decision-ready intelligence built around your questions.</p>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#1ca5dc] text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white hover:text-[#132b4d] transition-colors">
                Commission a report <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
